import { useState } from 'react';
import { type WeightedResult, type Criterion, type DecisionContext, type ScoringScale } from '../types';
import { FileText, FileJson, FileSpreadsheet, RotateCcw, Table } from 'lucide-react';
import { exportToExcel, sanitizeSheetName } from '../../../lib/excelService';

interface ResultsStepProps {
  results: WeightedResult[];
  criteria: Criterion[];
  context: DecisionContext | null;
  scale: ScoringScale;
  scoreMatrix: { [itemId: string]: { [criterionId: string]: number } };
  onReset: () => void;
}

export function ResultsStep({
  results,
  criteria,
  context,
  scale,
  scoreMatrix,
  onReset,
}: ResultsStepProps) {
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  const exportMarkdown = () => {
    let content = `# Simple Weighted Scoring Results`;

    if (context) {
      content += ` – ${context.name}\n\n`;
      content += `**Definition of success:** ${context.successDefinition}\n\n`;
      if (context.constraints) {
        content += `**Constraints:** ${context.constraints}\n\n`;
      }
    } else {
      content += `\n\n`;
    }

    content += `## Criteria & Weights\n\n`;
    criteria.forEach((c) => {
      content += `- **${c.name}** – ${c.weight}% – ${c.description}\n`;
    });

    content += `\n## Scorecard (scale ${scale.min}–${scale.max})\n\n`;
    content += `| Item | ${criteria.map((c) => c.name).join(' | ')} | Total |\n`;
    content += `|------|${criteria.map(() => '---').join('|')}|-------|\n`;

    results.forEach((result) => {
      const scores = criteria.map((c) => scoreMatrix[result.itemId]?.[c.id] ?? '-').join(' | ');
      content += `| ${result.itemName} | ${scores} | ${result.totalScore} |\n`;
    });

    content += `\n## Final Ranking\n\n`;
    results.forEach((result, index) => {
      content += `${index + 1}. **${result.itemName}** – ${result.totalScore}\n`;
    });

    content += `\n---\n`;
    content += `_Notes: This is a pseudo-quantitative method; results reflect the quality of the criteria, weights, and scores chosen._\n`;

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `weighted-scoring-results-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCSV = () => {
    const headers = ['Rank', 'Item', ...criteria.map((c) => c.name), 'Total Score'];
    const rows = results.map((result, index) => [
      index + 1,
      result.itemName,
      ...criteria.map((c) => scoreMatrix[result.itemId]?.[c.id] ?? ''),
      result.totalScore,
    ]);

    const csv = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `weighted-scoring-results-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportJSON = () => {
    const data = {
      context,
      criteria,
      scale,
      results: results.map((result, index) => ({
        rank: index + 1,
        itemName: result.itemName,
        totalScore: result.totalScore,
        scores: criteria.map((c) => ({
          criterion: c.name,
          score: scoreMatrix[result.itemId]?.[c.id] ?? null,
        })),
      })),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `weighted-scoring-results-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    // Results sheet with all scores
    const resultsData = results.map((result, index) => {
      const row: Record<string, unknown> = {
        rank: index + 1,
        item: result.itemName,
      };
      // Add each criterion score
      criteria.forEach((c) => {
        row[c.name] = scoreMatrix[result.itemId]?.[c.id] ?? '';
      });
      row['total_score'] = result.totalScore;
      return row;
    });

    // Criteria sheet with weights
    const criteriaData = criteria.map((c) => ({
      name: c.name,
      description: c.description || '',
      weight: c.weight,
      weight_percent: `${(c.weight * 100).toFixed(0)}%`,
    }));

    // Metadata sheet
    const metadataData = [
      { field: 'Context', value: context?.name || 'N/A' },
      { field: 'Success Definition', value: context?.successDefinition || 'N/A' },
      { field: 'Constraints', value: context?.constraints || 'N/A' },
      { field: 'Scale Min', value: String(scale.min) },
      { field: 'Scale Max', value: String(scale.max) },
      { field: 'Scale Labels', value: scale.labels?.join(', ') || 'N/A' },
      { field: 'Total Items', value: String(results.length) },
      { field: 'Total Criteria', value: String(criteria.length) },
      { field: 'Export Date', value: new Date().toISOString() },
    ];

    exportToExcel({
      filename: `weighted-scoring-results-${Date.now()}.xlsx`,
      sheets: [
        {
          name: sanitizeSheetName('Results'),
          data: resultsData,
          columns: [
            { key: 'rank', header: 'Rank', width: 8 },
            { key: 'item', header: 'Item', width: 40 },
            ...criteria.map((c) => ({ key: c.name, header: c.name, width: 12 })),
            { key: 'total_score', header: 'Total Score', width: 12 },
          ],
        },
        {
          name: sanitizeSheetName('Criteria'),
          data: criteriaData,
          columns: [
            { key: 'name', header: 'Criterion', width: 25 },
            { key: 'description', header: 'Description', width: 40 },
            { key: 'weight', header: 'Weight', width: 10 },
            { key: 'weight_percent', header: 'Weight %', width: 10 },
          ],
        },
        {
          name: sanitizeSheetName('Metadata'),
          data: metadataData,
          columns: [
            { key: 'field', header: 'Field', width: 25 },
            { key: 'value', header: 'Value', width: 50 },
          ],
        },
      ],
    });
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-2">
              Results
            </h2>
            <p className="text-sm text-[var(--text-sec)]">
              Final prioritized list based on weighted scores.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={exportMarkdown}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
              title="Export as Markdown"
            >
              <FileText className="h-4 w-4" />
              MD
            </button>
            <button
              onClick={exportCSV}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
              title="Export as CSV"
            >
              <FileSpreadsheet className="h-4 w-4" />
              CSV
            </button>
            <button
              onClick={exportJSON}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
              title="Export as JSON"
            >
              <FileJson className="h-4 w-4" />
              JSON
            </button>
            <button
              onClick={exportExcel}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] transition-colors"
              title="Export as Excel"
            >
              <Table className="h-4 w-4" />
              Excel
            </button>
            <button
              onClick={onReset}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          </div>
        </div>

        {context && (
          <div className="mb-6 p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
            <h3 className="font-semibold text-[var(--text-sec)] mb-1">{context.name}</h3>
            <p className="text-sm text-[var(--text-sec)]">{context.successDefinition}</p>
            {context.constraints && (
              <p className="text-sm text-[var(--text-sec)] mt-2">
                <strong>Constraints:</strong> {context.constraints}
              </p>
            )}
          </div>
        )}

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Final Ranking</h3>
          <div className="space-y-2">
            {results.map((result, index) => (
              <div
                key={result.itemId}
                className="border border-[var(--rule)] p-4 hover:bg-[var(--surface)] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-[var(--slate)] text-white rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-semibold text-[var(--text-pri)]">{result.itemName}</div>
                      <div className="text-sm text-[var(--text-sec)]">
                        Total Score: <span className="font-medium">{result.totalScore}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setExpandedItem(expandedItem === result.itemId ? null : result.itemId)}
                    className="text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)]"
                  >
                    {expandedItem === result.itemId ? 'Hide' : 'Show'} Details
                  </button>
                </div>
                {expandedItem === result.itemId && (
                  <div className="mt-4 pt-4 border-t border-[var(--rule)]">
                    <div className="grid grid-cols-2 gap-4">
                      {criteria.map((criterion) => {
                        const contribution = result.criterionScores[criterion.id];
                        return (
                          <div key={criterion.id} className="text-sm">
                            <div className="flex justify-between mb-1">
                              <span className="text-[var(--text-sec)]">{criterion.name}</span>
                              <span className="font-medium text-[var(--text-pri)]">
                                {contribution.score} × {criterion.weight}% = {contribution.weightedContribution}
                              </span>
                            </div>
                            <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
                              <div
                                className="bg-[var(--slate)] h-2 rounded-full"
                                style={{ width: `${(contribution.weightedContribution / result.totalScore) * 100}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 p-4 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
          <p className="text-sm text-[var(--mustard)]">
            <strong>Remember:</strong> This is a pseudo-quantitative method. The math reflects the judgments and weights you chose. Use this as a structured input to decision-making, not as a replacement for judgment.
          </p>
        </div>
      </div>
    </div>
  );
}

